import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@clerk/react';
import { useDiffitUserContext } from '../contexts/DiffitUserContext';

export const useTypeform = (typeformUrl, additionalContext = {}) => {
  const { user } = useUser(); // Clerk user data
  const { diffitUser, selectedOrg, isPremiumUser, userLoginType } = useDiffitUserContext();
  const location = useLocation();

  const enhancedTypeformUrl = useMemo(() => {
    if (!typeformUrl) return '';

    try {
      // Parse the base URL to separate any existing hash
      const url = new URL(typeformUrl);
      const baseUrl = `${url.protocol}//${url.host}${url.pathname}${url.search}`;

      // Collect standardized user information
      const standardParams = {
        // User identification (common Typeform field names)
        user_id: diffitUser?.id || '',
        email: diffitUser?.email || user?.primaryEmailAddress?.emailAddress || '',
        first_name: diffitUser?.given_name || user?.firstName || '',
        last_name: diffitUser?.family_name || user?.lastName || '',

        // Context information
        source_url: `${window.location.origin}${location.pathname}${location.search}`,
        page_path: location.pathname,
        referrer: document.referrer || '',
        timestamp: new Date().toISOString(),

        // User status and organization
        is_premium_user: isPremiumUser ? 'true' : 'false',
        user_login_type: userLoginType || '',
        organization_id: selectedOrg?.id || '',
        organization_name: selectedOrg?.name || '',

        // Technical information
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language || '',
      };

      // Merge with additional context
      const allParams = { ...standardParams, ...additionalContext };

      // Build hash parameters string (Typeform format: #param1=value1&param2=value2)
      const hashParams = Object.entries(allParams)
        // eslint-disable-next-line no-unused-vars
        .filter(([key, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => {
          // Encode the value properly for URL hash parameters
          const encodedValue = encodeURIComponent(String(value));
          return `${key}=${encodedValue}`;
        })
        .join('&');

      // Combine existing hash (if any) with new parameters
      let finalHash = '';
      if (url.hash) {
        // Remove the # from existing hash and combine with new params
        const existingHash = url.hash.substring(1);
        finalHash = hashParams ? `${existingHash}&${hashParams}` : existingHash;
      } else {
        finalHash = hashParams;
      }

      return finalHash ? `${baseUrl}#${finalHash}` : baseUrl;
    } catch (error) {
      console.error('Error creating Typeform URL:', error);
      return typeformUrl; // Return original URL if there's an error
    }
  }, [
    typeformUrl,
    diffitUser,
    user,
    selectedOrg,
    isPremiumUser,
    userLoginType,
    location.pathname,
    location.search,
    additionalContext,
  ]);

  return enhancedTypeformUrl;
};
